import { useState, useCallback, useEffect } from 'react';
import { useTrip } from '../../contexts/trip-context';
import dayService from '../../../services/day/day.service';

export interface IEditDayModel {
  id: string | null | undefined;
  dayNumber?: number;
  color: string;
  description?: string;
  notes?: string;
  title?: string;
  itineraryId: string;
  tripId: string;
}

export const useEditDay = (
  dayId: string,
  tripId: string,
  itineraryId: string,
  onSuccess: () => void
) => {
  const [day, setDay] = useState<IEditDayModel | null>(null);
  const [validated] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setRefreshTrip } = useTrip();

  // Initialize day data based on dayId
  useEffect(() => {
    if (dayId && tripId && itineraryId) {
      setIsLoading(true);

      // TODO: Implement actual API call to fetch day details
      // For now, we'll simulate the API call with mock data
      const fetchDayData = async () => {
        try {
          const dayData = await dayService.getDayById(
            tripId,
            itineraryId,
            dayId
          );

          const editDayData: IEditDayModel = {
            id: dayId,
            dayNumber: dayData.dayNumber || 1,
            color: dayData.color || '#842234',
            description: dayData.description || '',
            notes: dayData.notes || '',
            title: dayData.title || `Day ${dayData.dayNumber || 1}`,
            itineraryId: itineraryId,
            tripId: tripId
          };

          setDay(editDayData);
        } catch (error: any) {
          console.error('Error fetching day data:', error);
          setError('Failed to fetch day data');
        } finally {
          setIsLoading(false);
        }
      };

      fetchDayData();
    }
  }, [dayId, tripId, itineraryId]);

  const handleInputChange = useCallback(
    (field: keyof IEditDayModel, value: any) => {
      setDay((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [field]: value
        };
      });
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!day) {
      setError('No day data to update');
      return;
    }

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await dayService.updateDay(
        tripId,
        itineraryId,
        day.id!,
        day
      );
      if (result) {
        console.log('Day updated successfully:', result);
        setRefreshTrip(true);
        onSuccess();
      } else {
        setError('Day update failed');
      }
    } catch (error: any) {
      console.error('Error updating day:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update day. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitWithData = async (dayData: {
    color: string;
    title: string;
    description: string;
  }) => {
    if (!day) {
      setError('No day data to update');
      return;
    }

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const updateData: IEditDayModel = {
      ...day,
      color: dayData.color,
      title: dayData.title,
      description: dayData.description,
      notes: dayData.description // Use description as notes for now
    };

    try {
      const result = await dayService.updateDay(
        tripId,
        itineraryId,
        updateData.id!,
        updateData
      );
      if (result) {
        console.log('Day updated successfully:', result);
        setRefreshTrip(true);
        onSuccess();
      } else {
        setError('Day update failed');
      }
    } catch (error: any) {
      console.error('Error updating day:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update day. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    day,
    validated,
    isSubmitting,
    isLoading,
    error,
    handleInputChange,
    handleSubmit,
    handleSubmitWithData,
    resetError
  };
};
